import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { Sparkles, Check, ShoppingCart, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Bundle upsell shown after the customer applies a species via the Species Wizard.
 * The wizard sets `selectedSpeciesId` (passed in via the variant configurator) and we
 * look up the right kit profile via the backend's SPECIES_KIT_MAP.
 *
 * If no species was applied, we still show a default "Starter Kit" so we don't lose
 * the upsell opportunity for customers who skipped the wizard.
 */
const BundleUpsell = ({ selectedSpeciesId, defaultKit = "fossorial_gecko" }) => {
  const { user, token } = useAuth();
  const [data, setData] = useState(null);
  const [adding, setAdding] = useState(false);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/bundles`);
        if (!cancelled) setData(data);
      } catch (e) {
        // Silent — upsell is optional UI; don't pollute UX with errors.
        console.error("Bundle fetch error:", e?.message || e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const recommendedKit = useMemo(() => {
    if (!data) return null;
    const kitId = (selectedSpeciesId && data.species_kit_map[selectedSpeciesId]) || defaultKit;
    return data.bundles.find((b) => b.id === kitId);
  }, [data, selectedSpeciesId, defaultKit]);

  if (!recommendedKit) return null;

  const savings = (recommendedKit.rrp_total - recommendedKit.total).toFixed(2);

  const addBundleToCart = async () => {
    if (!user) {
      toast.error("Please log in to add the kit to your cart");
      return;
    }
    setAdding(true);
    try {
      await axios.post(
        `${API}/cart/add-bundle`,
        { bundle_id: recommendedKit.id, quantity: 1 },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`${recommendedKit.name} added to cart!`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't add bundle");
    } finally {
      setAdding(false);
    }
  };

  return (
    <div
      className="border border-amber-500/30 bg-gradient-to-br from-amber-500/5 via-[#0A0A0A] to-[#0A0A0A] mt-6"
      data-testid="bundle-upsell"
    >
      {/* Header */}
      <div className="p-4 border-b border-white/10">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-2.5">
            <div className="w-8 h-8 rounded-full bg-amber-500/15 flex items-center justify-center shrink-0">
              <Sparkles className="w-4 h-4 text-amber-400" />
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-widest text-amber-400 font-semibold">
                Complete your build
              </p>
              <p className="text-sm font-bold text-[#F4F1DE] leading-tight mt-0.5">
                {recommendedKit.name}
              </p>
              <p className="text-xs text-[#F4F1DE]/60 mt-0.5">{recommendedKit.tagline}</p>
            </div>
          </div>
          <div className="text-right shrink-0">
            <p className="text-[10px] line-through text-[#F4F1DE]/40">
              £{recommendedKit.rrp_total.toFixed(2)}
            </p>
            <p className="text-lg font-bold text-amber-400 leading-none">
              £{recommendedKit.total.toFixed(2)}
            </p>
            <p className="text-[10px] text-green-400 font-semibold">save £{savings}</p>
          </div>
        </div>
      </div>

      {/* Expandable item list */}
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center justify-between px-4 py-2 text-xs text-[#F4F1DE]/60 hover:text-[#F4F1DE] transition-colors"
        data-testid="bundle-upsell-toggle"
      >
        <span>{expanded ? "Hide" : "Show"} what's included ({recommendedKit.items.length} items)</span>
        {expanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
      </button>

      {expanded && (
        <ul className="px-4 pb-3 space-y-1.5">
          {recommendedKit.items.map((item) => (
            <li key={item.name} className="flex items-start gap-2 text-xs text-[#F4F1DE]/75">
              <Check className="w-3 h-3 text-amber-400 mt-0.5 shrink-0" />
              <span className="flex-1">{item.name}</span>
              <span className="text-[#F4F1DE]/40 tabular-nums">£{item.supplier_price.toFixed(2)}</span>
            </li>
          ))}
        </ul>
      )}

      {/* CTA */}
      <div className="p-3 border-t border-white/10">
        <button
          type="button"
          onClick={addBundleToCart}
          disabled={adding}
          className="w-full flex items-center justify-center gap-2 bg-amber-500 hover:bg-amber-600 text-black font-bold text-sm py-2.5 transition-colors disabled:opacity-50"
          data-testid="bundle-upsell-add-btn"
        >
          <ShoppingCart className="w-4 h-4" />
          {adding ? "Adding..." : `Add kit to my order — £${recommendedKit.total.toFixed(2)}`}
        </button>
      </div>
    </div>
  );
};

export default BundleUpsell;
